import { ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { SectionCard } from '../../../components/design-system/admin';

export interface EditorSectionProps {
  title: string;
  /** One line on what the section configures. */
  description?: ReactNode;
  /** A short count after the title ("12 attributes"). */
  badge?: ReactNode;
  /** At the header's end. */
  action?: ReactNode;
  /** The title folds the section away. */
  collapsible?: boolean;
  defaultExpanded?: boolean;
  /** Let a table run edge to edge. */
  flush?: boolean;
  contentClassName?: string;
  children: ReactNode;
  testId?: string;
}

/**
 * One block of a catalog editor: the kit's `SectionCard`, plus a count next to
 * the title and, when `collapsible`, a disclosure button that folds it away.
 * Shared by the product-family and product-type editors.
 */
export function EditorSection({
  title,
  description,
  badge,
  action,
  collapsible = false,
  defaultExpanded = true,
  flush,
  contentClassName,
  children,
  testId,
}: EditorSectionProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const contentId = useId();
  const open = !collapsible || expanded;

  const heading = (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
      {collapsible ? (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          aria-controls={contentId}
          title={expanded ? t('ADMIN_CATALOG.EDITOR.COLLAPSE') : t('ADMIN_CATALOG.EDITOR.EXPAND')}
          className="-ms-1 inline-flex items-center gap-1.5 rounded-md px-1 text-start outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          <ChevronDown
            aria-hidden
            className={cn(
              'size-4 text-muted-foreground transition-transform',
              !expanded && '-rotate-90 rtl:rotate-90',
            )}
          />
          {title}
        </button>
      ) : (
        title
      )}
      {badge && <span className="text-xs font-normal text-muted-foreground tabular-nums">{badge}</span>}
    </span>
  );

  return (
    <SectionCard
      title={heading}
      description={open ? description : undefined}
      action={action}
      flush={flush && open}
      contentClassName={cn(!open && 'hidden', contentClassName)}
      testId={testId}
    >
      <div id={contentId}>{open && children}</div>
    </SectionCard>
  );
}
